/**
 * PaymentSuccess.js — صفحة استلام الدفع لمنصة CloudGM Customer
 *
 * تُستقبل من بوابات الدفع (Tamara / Stripe / Tabby / Ziina) بعد الدفع الناجح.
 * تجلب بيانات الفاتورة من ASB606 Admin (API عام بلا توثيق):
 *   GET https://asb606.com/api/public/receipt/{invoice_id}
 *
 * Query params:
 *   ?inv=CGB-000023        (مطلوب — رقم الفاتورة)
 *   ?session_id=xxxx       (اختياري — من بوابة الدفع)
 *   ?lang=ar|en            (اختياري — يتخطّى اختيار المستخدم)
 *
 * تعالج الحالات:
 *   paid → إيصال مكتمل + Badge أخضر
 *   partially_paid → إيصال + المبلغ المتبقي
 *   unpaid → "الدفع قيد المعالجة..." + Polling كل 5 ثواني (5 محاولات)
 *   cancelled / expired → "الفاتورة ملغاة"
 *   404 → "الفاتورة غير موجودة"
 *   500 → "حاول لاحقاً" + زر إعادة
 *   invoice_id مفقود → صفحة خطأ عامة
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, AlertTriangle, Printer, ArrowLeft, ArrowRight, RefreshCw, Loader2, Home } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const ADMIN_API = 'https://asb606.com/api/public/receipt';
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 5;

// ─── translation strings (all inline — لا حاجة للـ i18n keys) ───
const T = {
  successTitle:      { ar: 'تم استلام الدفع بنجاح',            en: 'Payment Received Successfully' },
  successSubtitle:   { ar: 'شكراً لك — تفاصيل إيصالك أدناه',  en: 'Thank you — your receipt is below' },
  processingTitle:   { ar: 'الدفع قيد المعالجة…',              en: 'Payment is being processed…' },
  processingHint:    { ar: 'سيتم تحديث الحالة تلقائياً خلال ثوانٍ.', en: 'The status will update automatically in a few seconds.' },
  partialTitle:      { ar: 'الدفع الجزئي مستلَم',              en: 'Partial Payment Received' },
  cancelledTitle:    { ar: 'تم إلغاء الفاتورة',                en: 'Invoice Cancelled' },
  expiredTitle:      { ar: 'انتهت صلاحية الفاتورة',            en: 'Invoice Expired' },
  notFoundTitle:     { ar: 'الفاتورة غير موجودة',              en: 'Invoice Not Found' },
  notFoundHint:      { ar: 'تحقّق من الرابط أو تواصل مع الدعم.', en: 'Check the link or contact support.' },
  errorTitle:        { ar: 'حصل خلل مؤقت',                     en: 'A temporary error occurred' },
  errorHint:         { ar: 'حاول تحديث الصفحة أو المحاولة لاحقاً.', en: 'Try refreshing or try again later.' },
  missingInvoice:    { ar: 'رقم الفاتورة مفقود من الرابط',     en: 'Invoice number is missing from the URL' },
  invoiceNo:         { ar: 'رقم الإيصال',                      en: 'Receipt No.' },
  customer:          { ar: 'العميل',                            en: 'Customer' },
  paymentMethod:     { ar: 'طريقة الدفع',                      en: 'Payment Method' },
  paidAt:            { ar: 'وقت الدفع',                         en: 'Paid At' },
  service:           { ar: 'الخدمة',                            en: 'Service' },
  description:       { ar: 'الوصف',                             en: 'Description' },
  amount:            { ar: 'المبلغ',                            en: 'Amount' },
  subtotal:          { ar: 'المجموع الفرعي',                    en: 'Subtotal' },
  tax:               { ar: 'الضريبة',                           en: 'Tax' },
  total:             { ar: 'المجموع الإجمالي',                 en: 'Total' },
  paid:              { ar: 'المدفوع',                            en: 'Paid' },
  remaining:         { ar: 'المتبقي',                            en: 'Remaining' },
  statusPaid:        { ar: 'مدفوع',                             en: 'Paid' },
  statusPartial:     { ar: 'مدفوع جزئياً',                     en: 'Partially Paid' },
  statusUnpaid:      { ar: 'قيد المعالجة',                     en: 'Processing' },
  home:              { ar: 'العودة للرئيسية',                 en: 'Back to Home' },
  print:             { ar: 'طباعة الإيصال',                   en: 'Print Receipt' },
  retry:             { ar: 'إعادة المحاولة',                   en: 'Retry' },
  loading:           { ar: 'جاري تحميل الإيصال…',             en: 'Loading your receipt…' },
};

const fmtAED = (n, isAr) => {
  const val = Number(n || 0).toLocaleString(isAr ? 'ar-AE' : 'en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return isAr ? `${val} د.إ` : `${val} AED`;
};

const fmtDate = (iso, isAr) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? 'ar-AE' : 'en-AE', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  // ?lang= overrides user preference (بوابات الدفع قد تمرّرها)
  const langParam = params.get('lang');
  const isAr = (langParam === 'ar') || (!langParam && language === 'ar');
  const dir = isAr ? 'rtl' : 'ltr';
  const t = (k) => T[k][isAr ? 'ar' : 'en'];

  const invoiceId = params.get('inv') || params.get('invoice_id');
  const [state, setState] = useState('loading');   // loading | paid | partially_paid | unpaid | cancelled | expired | notfound | error | missing
  const [data, setData] = useState(null);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);

  const fetchReceipt = useCallback(async () => {
    if (!invoiceId) { setState('missing'); return; }
    try {
      const resp = await fetch(`${ADMIN_API}/${encodeURIComponent(invoiceId)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (resp.status === 404) { setState('notfound'); return; }
      if (!resp.ok) { setState('error'); return; }
      const json = await resp.json();
      setData(json);
      const s = (json.status || '').toLowerCase();
      if (s === 'paid') setState('paid');
      else if (s === 'partially_paid') setState('partially_paid');
      else if (s === 'unpaid' || s === 'pending' || s === 'processing') setState('unpaid');
      else if (s === 'cancelled' || s === 'canceled') setState('cancelled');
      else if (s === 'expired') setState('expired');
      else setState('paid'); // unknown-but-ok status → assume paid
    } catch (e) {
      setState('error');
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchReceipt();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // Polling for `unpaid` — check up to 5 times, every 5s.
  useEffect(() => {
    if (state === 'unpaid' && pollCount.current < MAX_POLL_ATTEMPTS) {
      pollTimer.current = setTimeout(() => {
        pollCount.current += 1;
        fetchReceipt();
      }, POLL_INTERVAL_MS);
    }
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [state, fetchReceipt]);

  // Paid customer ad (reference = unified receipt CGC-YYYY-NNNNNNN): confirm + activate once the gateway reports success
  const [adActivation, setAdActivation] = useState(null); // null | 'checking' | 'active' | 'pending'
  useEffect(() => {
    if (state !== 'paid') return;
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem('pending_ad_payment') || 'null'); } catch { pending = null; }
    const isAdRef = Boolean(pending?.ad_id) && (!invoiceId || pending?.reference === invoiceId);
    if (!isAdRef) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setAdActivation('checking');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/ads/${pending.ad_id}/confirm-payment`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.ok) { localStorage.removeItem('pending_ad_payment'); setAdActivation('active'); }
        else setAdActivation('pending');
      })
      .catch(() => setAdActivation('pending'));
  }, [state, invoiceId]);

  // ─── Style helpers ───
  const BLUE = '#3B82F6';
  const bg = isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]';
  const card = isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white border border-[#E2E8F0] shadow-sm';
  const textMain = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-[#64748B]';
  const border = isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  return (
    <div className={`min-h-screen ${bg}`} dir={dir} data-testid="payment-success-page">
      {/* Print stylesheet — يخفي الأزرار وقت الطباعة */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-card { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <div className="fixed inset-0 pointer-events-none no-print">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#3B82F6]/10' : 'bg-[#3B82F6]/5'} rounded-full blur-[100px]`} />
      </div>

      <section className="relative pt-10 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {state === 'loading' && (
            <div className={`rounded-2xl p-12 flex flex-col items-center gap-4 ${card}`}>
              <Loader2 className="h-12 w-12 animate-spin" style={{ color: BLUE }} />
              <p className={`text-sm ${textMuted}`}>{t('loading')}</p>
            </div>
          )}

          {state === 'missing' && (
            <StatusCard
              icon={<AlertTriangle className="h-16 w-16 text-amber-500" />}
              title={t('missingInvoice')}
              hint=""
              card={card} textMain={textMain} textMuted={textMuted}
            >
              <PrimaryHomeButton isAr={isAr} t={t} BackArrow={BackArrow} BLUE={BLUE} />
            </StatusCard>
          )}

          {state === 'notfound' && (
            <StatusCard
              icon={<XCircle className="h-16 w-16 text-red-500" />}
              title={t('notFoundTitle')}
              hint={`${t('notFoundHint')} ${invoiceId ? `(${invoiceId})` : ''}`}
              card={card} textMain={textMain} textMuted={textMuted}
            >
              <PrimaryHomeButton isAr={isAr} t={t} BackArrow={BackArrow} BLUE={BLUE} />
            </StatusCard>
          )}

          {state === 'error' && (
            <StatusCard
              icon={<AlertTriangle className="h-16 w-16 text-amber-500" />}
              title={t('errorTitle')}
              hint={t('errorHint')}
              card={card} textMain={textMain} textMuted={textMuted}
            >
              <button
                onClick={() => { setState('loading'); fetchReceipt(); }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-colors"
                style={{ backgroundColor: BLUE }}
                data-testid="payment-retry-btn"
              >
                <RefreshCw className="h-4 w-4" /> {t('retry')}
              </button>
            </StatusCard>
          )}

          {(state === 'cancelled' || state === 'expired') && (
            <StatusCard
              icon={<XCircle className="h-16 w-16 text-red-500" />}
              title={state === 'expired' ? t('expiredTitle') : t('cancelledTitle')}
              hint={data?.invoice_no || invoiceId || ''}
              card={card} textMain={textMain} textMuted={textMuted}
            >
              <PrimaryHomeButton isAr={isAr} t={t} BackArrow={BackArrow} BLUE={BLUE} />
            </StatusCard>
          )}

          {state === 'unpaid' && (
            <StatusCard
              icon={<Clock className="h-16 w-16" style={{ color: BLUE }} />}
              title={t('processingTitle')}
              hint={`${t('processingHint')} (${pollCount.current + 1}/${MAX_POLL_ATTEMPTS})`}
              card={card} textMain={textMain} textMuted={textMuted}
            >
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: BLUE }} />
                <span className={textMuted}>{data?.invoice_no || invoiceId}</span>
              </div>
            </StatusCard>
          )}

          {(state === 'paid' || state === 'partially_paid') && data && (
            <>
              {adActivation && (
                <div className={`mb-4 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap ${card}`} data-testid="ad-activation-banner">
                  <p className={`text-sm font-medium ${textMain}`}>
                    {adActivation === 'checking' && (isAr ? 'جارٍ تفعيل إعلانك…' : 'Activating your ad…')}
                    {adActivation === 'active' && (isAr ? '✅ تم تفعيل إعلانك بنجاح' : '✅ Your ad is now active')}
                    {adActivation === 'pending' && (isAr ? 'تم استلام الدفع — سيُفعَّل إعلانك خلال لحظات، أو اضغط «تحقق من الدفع» في إعلاناتي.' : 'Payment received — your ad will activate shortly, or press “Check payment” in My Ads.')}
                  </p>
                  <Link to="/dashboard?tab=myads" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: BLUE }} data-testid="goto-my-ads-btn">
                    {isAr ? 'عرض إعلاناتي' : 'View my ads'}
                  </Link>
                </div>
              )}
              <ReceiptCard
              data={data}
              state={state}
              isAr={isAr}
              t={t}
              card={card}
              textMain={textMain}
              textMuted={textMuted}
              border={border}
              BackArrow={BackArrow}
              BLUE={BLUE}
              isDarkMode={isDarkMode}
            />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// ─── Sub-components ───

function StatusCard({ icon, title, hint, children, card, textMain, textMuted }) {
  return (
    <div className={`rounded-2xl p-8 md:p-12 flex flex-col items-center text-center gap-4 print-card ${card}`}>
      {icon}
      <h1 className={`text-2xl sm:text-3xl font-bold ${textMain}`}>{title}</h1>
      {hint && <p className={`${textMuted} max-w-lg`}>{hint}</p>}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3 no-print">
        {children}
      </div>
    </div>
  );
}

function PrimaryHomeButton({ isAr, t, BackArrow, BLUE }) {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-colors"
      style={{ backgroundColor: BLUE }}
      data-testid="payment-home-btn"
    >
      <Home className="h-4 w-4" />
      {t('home')}
      <BackArrow className="h-4 w-4" />
    </Link>
  );
}

function ReceiptCard({ data, state, isAr, t, card, textMain, textMuted, border, BackArrow, BLUE, isDarkMode }) {
  const isPartial = state === 'partially_paid';
  const services = Array.isArray(data.services) ? data.services : [];
  const paymentMethod = isAr ? (data.payment_method_ar || data.payment_method) : data.payment_method;
  const customerName = data.customer?.name || '';
  const customerEmail = data.customer?.email || '';
  const customerPhone = data.customer?.phone || '';

  return (
    <div className={`rounded-2xl overflow-hidden print-card ${card}`}>
      {/* Header — Success or Partial */}
      <div className="p-6 md:p-10 text-center" style={{ background: `linear-gradient(180deg, ${BLUE}20, transparent)` }}>
        <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BLUE}20` }}>
          <CheckCircle2 className="h-12 w-12" style={{ color: BLUE }} />
        </div>
        <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${textMain}`} data-testid="payment-success-title">
          {isPartial ? t('partialTitle') : t('successTitle')}
        </h1>
        <p className={`text-sm ${textMuted}`}>{t('successSubtitle')}</p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
             style={{ backgroundColor: isPartial ? '#F59E0B20' : '#10B98120', color: isPartial ? '#F59E0B' : '#10B981' }}>
          <span>{isPartial ? t('statusPartial') : t('statusPaid')}</span>
        </div>
      </div>

      {/* Meta row */}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 p-6 md:p-8 border-y ${border}`}>
        <MetaCell label={t('invoiceNo')} value={data.invoice_no || '—'} textMuted={textMuted} textMain={textMain} testId="receipt-invoice-no" />
        <MetaCell label={t('customer')} value={customerName || '—'} textMuted={textMuted} textMain={textMain} />
        {paymentMethod && <MetaCell label={t('paymentMethod')} value={paymentMethod} textMuted={textMuted} textMain={textMain} />}
        {data.paid_at && <MetaCell label={t('paidAt')} value={fmtDate(data.paid_at, isAr)} textMuted={textMuted} textMain={textMain} />}
      </div>

      {/* Customer contact — small row */}
      {(customerEmail || customerPhone) && (
        <div className={`px-6 md:px-8 py-3 text-xs ${textMuted} flex flex-wrap gap-x-6 gap-y-1`} dir="ltr">
          {customerEmail && <span>{customerEmail}</span>}
          {customerPhone && <span>{customerPhone}</span>}
        </div>
      )}

      {/* Services table */}
      {services.length > 0 && (
        <div className="p-6 md:p-8">
          <div className={`rounded-xl overflow-hidden border ${border}`}>
            <table className="w-full text-sm">
              <thead className={isDarkMode ? 'bg-white/5' : 'bg-slate-50'}>
                <tr>
                  <th className={`text-start p-3 font-medium ${textMuted}`}>{t('service')}</th>
                  <th className={`text-start p-3 font-medium ${textMuted} hidden sm:table-cell`}>{t('description')}</th>
                  <th className={`text-end p-3 font-medium ${textMuted}`}>{t('amount')}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s, i) => (
                  <tr key={i} className={i > 0 ? `border-t ${border}` : ''}>
                    <td className={`p-3 ${textMain}`}>{isAr ? (s.service_type_ar || s.service_type_en) : (s.service_type_en || s.service_type_ar)}</td>
                    <td className={`p-3 ${textMuted} hidden sm:table-cell`}>{s.description || '—'}</td>
                    <td className={`p-3 text-end font-medium ${textMain}`}>{fmtAED(s.amount, isAr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Totals */}
      <div className={`p-6 md:p-8 border-t ${border} space-y-2 text-sm`}>
        <TotalRow label={t('subtotal')} value={fmtAED(data.subtotal, isAr)} textMuted={textMuted} textMain={textMain} />
        {data.tax_amount ? (
          <TotalRow
            label={`${t('tax')} ${data.tax_percent ? `(${data.tax_percent}%)` : ''}`}
            value={fmtAED(data.tax_amount, isAr)}
            textMuted={textMuted}
            textMain={textMain}
          />
        ) : null}
        <div className={`flex justify-between items-center pt-3 border-t ${border} text-lg font-bold ${textMain}`}>
          <span>{t('total')}</span>
          <span data-testid="receipt-total">{fmtAED(data.total, isAr)}</span>
        </div>
        {isPartial && (
          <>
            <TotalRow label={t('paid')} value={fmtAED(data.paid_amount, isAr)} textMuted={textMuted} textMain="text-emerald-500" />
            <TotalRow label={t('remaining')} value={fmtAED(data.remaining_amount, isAr)} textMuted={textMuted} textMain="text-amber-500" />
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className={`p-6 md:p-8 border-t ${border} flex flex-col sm:flex-row items-center gap-3 no-print`}>
        <Link
          to="/"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-colors flex-1"
          style={{ backgroundColor: BLUE }}
          data-testid="payment-home-btn"
        >
          <Home className="h-4 w-4" />
          {t('home')}
          <BackArrow className="h-4 w-4" />
        </Link>
        <button
          onClick={() => window.print()}
          className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
            isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-[#0F172A]'
          }`}
          data-testid="payment-print-btn"
        >
          <Printer className="h-4 w-4" />
          {t('print')}
        </button>
      </div>
    </div>
  );
}

function MetaCell({ label, value, textMuted, textMain, testId }) {
  return (
    <div>
      <p className={`text-xs mb-1 ${textMuted}`}>{label}</p>
      <p className={`text-sm font-medium ${textMain}`} data-testid={testId}>{value}</p>
    </div>
  );
}

function TotalRow({ label, value, textMuted, textMain }) {
  return (
    <div className="flex justify-between items-center">
      <span className={textMuted}>{label}</span>
      <span className={`font-medium ${textMain}`}>{value}</span>
    </div>
  );
}
